import { images } from "@/assets/img/imagesMap";
import { cn } from "@/utils/cn";
import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface GroupMultiSelectProps {
    label?: string;
    selectedGroupIds: string[];
    onChange: (groupIds: string[]) => void;
    groups: Array<{ group_id: string; group_name: string }>;
    disabled?: boolean;
    className?: string;
}

export default function GroupMultiSelect({
    label = "관리 그룹",
    selectedGroupIds,
    onChange,
    groups,
    disabled = false,
    className = "",
}: GroupMultiSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownDirection, setDropdownDirection] = useState<'down' | 'up'>('down');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);


    // 드롭다운 방향 결정
    useEffect(() => {
        if (isOpen && buttonRef.current) {
            const buttonRect = buttonRef.current.getBoundingClientRect();
            const dropdownHeight = 200; // max-h-[200px]
            const spaceBelow = window.innerHeight - buttonRect.bottom;
            const spaceAbove = buttonRect.top;

            // 아래 공간이 부족하고 위 공간이 충분하면 위로 열기
            if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                setDropdownDirection('up');
            } else {
                setDropdownDirection('down');
            }
        }
    }, [isOpen]);



    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const sortedGroups = groups
        .filter(group => group.group_id !== null)
        .sort((a, b) => a.group_name.localeCompare(b.group_name));

    const handleToggle = (groupId: string) => {
        onChange(
            selectedGroupIds.includes(groupId)
                ? selectedGroupIds.filter(id => id !== groupId)
                : [...selectedGroupIds, groupId]
        );
    };

    return (
        <div ref={dropdownRef} className={`relative w-[140px] ${className}`}>
            <button
                ref={buttonRef}
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className="w-full h-[35px] px-[15px] border border-[#D6D6DE] rounded-[5px] 
                   text-[15px] font-pretendard text-left flex items-center justify-between 
                   disabled:cursor-not-allowed bg-white"
            >
                <span className="text-text-sub font-pretendard text-[14px]">
                    {label}
                </span>
                <img
                    src={images.icon_chevron_down_fill_gray}
                    alt=""
                    aria-hidden="true"
                    className={cn(
                        "absolute right-2 top-1/2 -translate-y-1/2 w-[0.8em] h-[0.8em] pointer-events-none transition-transform",
                        isOpen && dropdownDirection === 'up' && "rotate-180"
                    )}
                />
            </button>

            {isOpen && (
                <div className={cn(
                    "absolute z-50 w-full bg-white border border-[#D6D6DE] rounded-[5px] shadow-lg max-h-[200px] overflow-y-auto",
                    dropdownDirection === 'down' ? 'mt-1 top-full' : 'mb-1 bottom-full'
                )}>
                    {sortedGroups.length > 0 ? (
                        sortedGroups.map((group) => {
                            const isChecked = selectedGroupIds.includes(group.group_id);

                            return (
                                <label
                                    key={group.group_id}
                                    className={cn(
                                        "flex items-center px-[9px] py-2 cursor-pointer transition-colors",
                                        isChecked ? 'bg-[#DEF3FF]' : 'hover:bg-[#F5F5F5]'
                                    )}
                                >
                                    {/* 숨겨진 실제 체크박스 (접근성) */}
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleToggle(group.group_id)}
                                        className="sr-only"
                                    />

                                    {/* 커스텀 체크박스 */}
                                    <div className={cn(
                                        "w-[13px] h-[13px] border flex items-center justify-center mr-[10px] transition-colors",
                                        isChecked
                                            ? 'bg-sig border-sig'
                                            : 'bg-white border-[#C2C2C2]'
                                    )}>
                                        {isChecked && (
                                            <Check className="text-white" strokeWidth={3} />
                                        )}
                                    </div>

                                    <span className="text-[12px] text-text-sub font-pretendard font-medium">
                                        {group.group_name}
                                    </span>
                                </label>
                            );
                        })
                    ) : (
                        <div className="px-4 py-2 text-[13px] text-[#939393] font-pretendard">
                            그룹이 없습니다.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}